/**
 * 语言识别（PRD §8.4、4.1、§5.18）：
 * 账户设置 → 链接中的语言参数 → 浏览器语言 → 设备地区 → 英语；只在已开启的韩、日、英、中中匹配。
 */
import { DEFAULT_LANG, LANGS, normalizeLang, type Lang } from './langs.js';

/** 链接中的语言参数名，例如 https://ai-coin.co/?lang=ko */
export const LANG_QUERY_PARAM = 'lang';

/** 设备地区 → 语言。只收录与四种语言直接对应的地区。 */
export const REGION_LANG: Readonly<Record<string, Lang>> = {
  KR: 'ko',
  JP: 'ja',
  CN: 'zh',
  TW: 'zh',
  HK: 'zh',
  MO: 'zh',
};

/** 设备时区 → 地区，用于浏览器拿不到地区时推断设备地区。 */
export const TIMEZONE_REGION: Readonly<Record<string, string>> = {
  'Asia/Seoul': 'KR',
  'Asia/Tokyo': 'JP',
  'Asia/Shanghai': 'CN',
  'Asia/Chongqing': 'CN',
  'Asia/Harbin': 'CN',
  'Asia/Urumqi': 'CN',
  'Asia/Hong_Kong': 'HK',
  'Asia/Macau': 'MO',
  'Asia/Taipei': 'TW',
};

export interface DetectLanguageInput {
  /** 账户设置中的语言 */
  account?: string | null;
  /** 链接中的语言参数 */
  url?: string | null;
  /** 浏览器语言列表（navigator.languages），按优先级排列 */
  browser?: readonly string[];
  /** 设备地区（ISO 3166-1 alpha-2），按优先级排列 */
  regions?: readonly string[];
  /** 已开启的语言（A55 逐个开关）；默认四种全开 */
  enabled?: readonly Lang[];
}

export function detectLanguage(input: DetectLanguageInput = {}): Lang {
  const enabled = input.enabled && input.enabled.length > 0 ? input.enabled : LANGS;
  const pick = (tag: string | null | undefined): Lang | null => {
    const lang = normalizeLang(tag);
    return lang && enabled.includes(lang) ? lang : null;
  };

  const fromAccount = pick(input.account);
  if (fromAccount) return fromAccount;

  const fromUrl = pick(input.url);
  if (fromUrl) return fromUrl;

  for (const tag of input.browser ?? []) {
    const lang = pick(tag);
    if (lang) return lang;
  }

  for (const region of input.regions ?? []) {
    const lang = REGION_LANG[region.trim().toUpperCase()];
    if (lang && enabled.includes(lang)) return lang;
  }

  if (enabled.includes(DEFAULT_LANG)) return DEFAULT_LANG;
  // 英语被关闭时，退到第一个开启的语言
  return enabled[0] ?? DEFAULT_LANG;
}

/** 从语言标签中取地区子标签，例如 en-KR → KR。 */
export function regionOfLocaleTag(tag: string | null | undefined): string | null {
  if (!tag) return null;
  try {
    const region = new Intl.Locale(tag).region;
    return region ? region.toUpperCase() : null;
  } catch {
    return null;
  }
}

export interface BrowserEnvironment {
  /** location.search */
  search?: string;
  /** navigator.languages */
  languages?: readonly string[];
  /** navigator.language */
  language?: string;
  /** Intl.DateTimeFormat().resolvedOptions() 的 locale 与 timeZone */
  resolvedLocale?: string;
  timeZone?: string;
}

/** 浏览器端组装识别输入。account 由调用方（已登录时）传入。 */
export function detectFromBrowser(
  env: BrowserEnvironment,
  options: { account?: string | null; enabled?: readonly Lang[] } = {},
): Lang {
  const url = env.search ? new URLSearchParams(env.search).get(LANG_QUERY_PARAM) : null;
  const browser = env.languages && env.languages.length > 0 ? env.languages : env.language ? [env.language] : [];
  const regions: string[] = [];
  const localeRegion = regionOfLocaleTag(env.resolvedLocale);
  if (localeRegion) regions.push(localeRegion);
  for (const tag of browser) {
    const region = regionOfLocaleTag(tag);
    if (region && !regions.includes(region)) regions.push(region);
  }
  const tzRegion = env.timeZone ? TIMEZONE_REGION[env.timeZone] : undefined;
  if (tzRegion && !regions.includes(tzRegion)) regions.push(tzRegion);
  return detectLanguage({
    account: options.account ?? null,
    url,
    browser,
    regions,
    ...(options.enabled ? { enabled: options.enabled } : {}),
  });
}
